import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useAuthStore } from "../stores/auth";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean;
    title?: string;
    layout?: "app" | "bare";
    /** Minimum role required. 'reporter' = reporter OR admin. 'admin' = admin only. */
    requiresRole?: "admin" | "reporter";
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("../pages/Login.vue"),
    meta: { requiresAuth: false, layout: "bare", title: "Đăng nhập" },
  },
  {
    path: "/",
    name: "dashboard",
    component: () => import("../pages/Dashboard.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Dashboard", requiresRole: "admin" },
  },
  {
    path: "/complaints",
    name: "complaints-list",
    component: () => import("../pages/ComplaintsList.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Danh sách khiếu nại", requiresRole: "admin" },
  },
  {
    path: "/complaints/new",
    name: "complaint-new",
    component: () => import("../pages/ComplaintNew.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Tạo khiếu nại mới", requiresRole: "reporter" },
  },
  {
    path: "/complaints/:id",
    name: "complaint-detail",
    component: () => import("../pages/ComplaintDetail.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Chi tiết khiếu nại", requiresRole: "reporter" },
  },
  {
    path: "/my-complaints",
    name: "my-complaints",
    component: () => import("../pages/MyComplaints.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Khiếu nại của tôi", requiresRole: "reporter" },
  },
  {
    path: "/admin/products",
    name: "admin-products",
    component: () => import("../pages/AdminProducts.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Quản lý sản phẩm", requiresRole: "admin" },
  },
  {
    path: "/admin/recalled-lots",
    name: "admin-recalled-lots",
    component: () => import("../pages/admin/RecalledLotsList.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Quản lý lô thu hồi", requiresRole: "admin" },
  },
  {
    path: "/admin/recalled-lots/new",
    name: "admin-recalled-lots-new",
    component: () => import("../pages/admin/RecalledLotEdit.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Thêm lô thu hồi", requiresRole: "admin" },
  },
  {
    path: "/admin/recalled-lots/:id/edit",
    name: "admin-recalled-lots-edit",
    component: () => import("../pages/admin/RecalledLotEdit.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Sửa lô thu hồi", requiresRole: "admin" },
  },
  {
    path: "/admin/api-keys",
    name: "admin-api-keys",
    component: () => import("../pages/AdminApiKeys.vue"),
    meta: { requiresAuth: true, layout: "app", title: "API Keys", requiresRole: "admin" },
  },
  {
    path: "/admin/users",
    name: "admin-users",
    component: () => import("../pages/AdminUsers.vue"),
    meta: { requiresAuth: true, layout: "app", title: "Người dùng", requiresRole: "admin" },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "dashboard" },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (auth.user === null) {
    auth.bootstrap();
  }

  const requiresAuth = to.meta.requiresAuth !== false;

  if (requiresAuth && !auth.isAuthenticated) {
    sessionStorage.setItem("kff-return-to", to.fullPath);
    // Pre-warm GIS script before the login page even mounts so the button
    // appears faster. The login component's loadGisScript() detects this tag
    // and waits for it instead of injecting a second one.
    if (!document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      const s = Object.assign(document.createElement("script"), {
        src: "https://accounts.google.com/gsi/client",
        async: true,
        defer: true,
      });
      document.head.appendChild(s);
    }
    return { name: "login" };
  }

  // Pending users can't access any app page — send back to login
  // where GoogleSignInButton will show the approval message.
  if (requiresAuth && auth.isPending) {
    return { name: "login" };
  }

  // Role-based guards for authenticated non-pending users.
  if (requiresAuth && auth.isAuthenticated && !auth.isPending) {
    const requiredRole = to.meta.requiresRole;
    if (requiredRole === "admin" && !auth.isAdmin) {
      // Reporter trying to reach an admin-only page → their landing page.
      return { name: "my-complaints" };
    }
    // 'reporter' routes are accessible to both admin and reporter — no extra check needed.
  }

  // Redirect already-logged-in users away from the login page.
  if (to.name === "login" && auth.isAuthenticated && !auth.isPending) {
    return auth.isAdmin
      ? { name: "dashboard" }
      : { name: "my-complaints" };
  }

  return true;
});

router.afterEach((to) => {
  const base = "KFF Complaints";
  document.title = to.meta.title ? `${to.meta.title} — ${base}` : base;
});
